/** Investor profile copy. Part of `UI_STRINGS` - see ui-strings.ts. */
export const UI_INVESTOR_STRINGS = {
  INVESTOR_PROFILE: {
    EDIT_PROFILE: 'Edit Profile',
    SAVE: 'Save',
    CANCEL: 'Cancel',
    PRIVATE_BADGE: 'Private Profile',
    UPLOAD_HINT: 'Click to upload',
    PHOTO_VIEW_CAPTION: 'Contact Photo',
    PHOTO_TOO_LARGE: 'Image is too large. Please choose a smaller file.',
    PHOTO_UPLOAD_ERROR: 'Upload failed. Please try again.',
    IDENTITY: 'Personal Info',
    FUND_NAME: 'Fund Name',
    // The entity name the person was invited under, shown read-only above Fund Name (FR-187).
    FIRM_NAME: 'Firm Name',
    INVESTOR_TYPE: 'Investor Type',
    CONTACT_NAME: 'Contact Name',
    CONTACT_TITLE: 'Contact Title',
    EMAIL: 'Email',
    EMAIL_LOCKED_HINT: 'Email cannot be changed here.',
    PHONE: 'Phone',
    // Removes a phone saved as free text that cannot be read as a number (shown beside the field).
    REMOVE_SAVED_PHONE: 'Remove saved phone',
    WEBSITE: 'Website',
    LINKEDIN: 'LinkedIn',
    BIO: 'Bio / Intro',
    LOCATION: 'Location',
    ADDRESS_STREET_1: 'Address Street 1',
    ADDRESS_STREET_2: 'Address Street 2',
    CITY: 'City',
    STATE: 'State',
    POSTAL_CODE: 'Postal Code',
    COUNTRY: 'Country',
    // Location dropdowns (FR-188): the State list follows the chosen Country.
    SELECT_COUNTRY: 'Select a country',
    SELECT_STATE: 'Select a state',
    STATE_PICK_COUNTRY_FIRST: 'Select a country first',
    STATE_NONE_FOR_COUNTRY: 'No states for this country',
    // Clears a saved State the lists cannot offer (a legacy value, or a country with no states).
    REMOVE_SAVED_STATE: 'Remove saved state',
    PREFERENCES: 'Investment Preferences',
    PREFERENCES_HINT: 'These preferences are used for Capital Raise matching - keep them accurate.',
    FOUNDER_PREFERENCES: 'Founder Preferences',
    FEMALE_LED_STARTUPS: 'Female-Led Startups',
    UNDERREPRESENTED_FOUNDERS: 'Underrepresented Founders',
    UNDERREPRESENTED_CATEGORIES: 'Underrepresented Categories',
    UNDERREPRESENTED_CATEGORIES_HINT: 'Select all that apply.',
    ONLY_INVESTS_IN: 'Only Invests In',
    OPEN_TO: 'Open To',
    NO_PREFERENCE: 'No Preference',
    OPEN_TO_FEMALE_LED: 'Open to Female-Led Startups',
    OPEN_TO_UNDERREPRESENTED: 'Open to Underrepresented Founders',
    TICKET_SIZE: 'Ticket Size',
    MIN_INVESTMENT: 'Min Investment',
    MAX_INVESTMENT: 'Max Investment',
    MIN_REVENUE: 'Minimum Revenue Requirement',
    MIN_REVENUE_HINT: 'Leave blank for no minimum requirement',
    RECURRING_REVENUE: 'Recurring Revenue Preference',
    LEAD_OR_FOLLOW: 'Lead or Follow',
    ACTIVELY_DEPLOYING: 'Actively Deploying Capital',
    MARKET_MODEL_PREFERENCES: 'Market Model Preferences',
    ONLY: 'Only',
    SOMETIMES: 'Sometimes',
    NEVER: 'Never',
    OTHER_CRITERIA: 'Other Criteria',
    PREFERRED_STAGES: 'Preferred Funding Stages',
    PREFERRED_STAGES_HINT: 'Select all stages you invest in.',
    OFFICE_HOURS_INTEREST: 'Interested in Investor Office Hours',
    FOUNDER_FUNDER_EVENTS_INTEREST: 'Interested in Founder-Funder Events',
    INDUSTRIES: 'Preferred Industries',
    INDUSTRIES_HINT: 'Industries you actively invest in - used for matching.',
    ADD_INDUSTRIES: 'Add Industries',
    MAX_INDUSTRIES_HINT: `Maximum ${10} industries`,
    GEOGRAPHIES: 'Preferred Geographies',
    GEOGRAPHIES_HINT: 'Regions, countries, or states you invest in.',
    COLLEAGUE_CONTACTS: 'Colleague contacts by industry',
    COLLEAGUE_CONTACTS_HINT:
      'Name a colleague for each sector so the right person is known for sector-specific opportunities. Contacts only - no account is created and no email is sent.',
    COLLEAGUE: 'Colleague',
    COLLEAGUE_SECTOR: 'Sector',
    COLLEAGUE_SECTOR_PLACEHOLDER: 'Select a sector',
    COLLEAGUE_NAME: 'Colleague name',
    COLLEAGUE_EMAIL: 'Colleague email',
    ADD_COLLEAGUE: 'Add colleague',
    /** Row context for assistive tech: groups one colleague's fields and names each field by its row. */
    COLLEAGUE_ROW: (row: number) => `Colleague contact ${row}`,
    COLLEAGUE_ROW_FIELD: (field: string, row: number) => `${field}, colleague contact ${row}`,
    /**
     * Inline row errors. The first two answer a rejected save (the form is
     * still open, so no toast); the rest are the form's own validation. The
     * duplicate-sector text is shared by both, so there is one wording.
     */
    COLLEAGUE_ERRORS: {
      COLLEAGUE_CONTACT_DUPLICATE_SECTOR: 'Another row already uses this sector. Use one row per sector.',
      SECTOR_NOT_FOUND: 'That sector is no longer available. Choose another one.',
      SECTOR_REQUIRED: 'Sector is required',
      NAME_REQUIRED: 'Colleague name is required',
      NAME_TOO_LONG: 'Maximum 200 characters',
      EMAIL_REQUIRED: 'Colleague email is required',
      EMAIL_INVALID: 'Enter a valid email address',
      EMAIL_TOO_LONG: 'Maximum 320 characters',
    },
    REGION: 'Region',
    /**
     * Format-only validation messages (FR-186, FR-188). Plain language: the
     * validation library's own wording (for example "Expected number, received
     * nan") must never reach the screen.
     */
    ERRORS: {
      COUNTRY_REQUIRED: 'Select a country',
      REGION_REQUIRED: 'Region is required',
      STATE_INVALID: 'Select a valid state',
      CHOICE_INVALID: 'Choose one of the options',
      URL_INVALID: 'Enter a valid URL',
      AMOUNT_RANGE: 'Max Investment cannot be below Min Investment.',
      INDUSTRIES_MAX: (max: number) => `Maximum ${max} industries`,
    },
    ADD_GEOGRAPHY: 'Add Geography',
    REMOVE: 'Remove',
    DELETE_PROFILE: 'Delete Profile',
    DELETE_CONFIRM_TITLE: 'Delete investor profile?',
    DELETE_CONFIRM_BODY: 'This removes your fund profile and all preferences. This cannot be undone.',
    DELETE_CONFIRM_ACTION: 'Delete Profile',
    SAVE_SUCCESS: 'Your investor profile changes have been saved.',
    SAVE_ERROR: 'Something went wrong saving your profile. Please try again.',
    DELETE_SUCCESS: 'Your investor profile has been deleted.',
    DELETE_ERROR: 'Something went wrong deleting your profile. Please try again.',
  },
} as const;
